import React, { useState, useEffect, useRef, useCallback } from 'react';
import { channelsAPI } from '../lib/api';
import { Input } from './ui/input';
import { User, Briefcase, Users2, FileText, Receipt, Package, Truck, ClipboardList } from 'lucide-react';

const CATEGORY_ICONS = {
  user: User,
  job: Briefcase,
  customer: Users2,
  estimate: FileText,
  invoice: Receipt,
  part: Package,
  vehicle: Truck,
  po: ClipboardList,
};

const CATEGORY_LABELS = {
  user: 'Team Members',
  job: 'Jobs',
  customer: 'Customers',
  estimate: 'Estimates',
  invoice: 'Invoices',
  part: 'Parts',
  vehicle: 'Vehicles',
  po: 'Purchase Orders',
};

export function MentionComposer({ value, onChange, onSubmit, placeholder, disabled, onPasteImage }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeout = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1) { setSuggestions([]); return; }
    try {
      const res = await channelsAPI.searchMentions(q, 'user,job,customer,estimate,invoice,part,vehicle,po');
      setSuggestions(res.data || []);
      setActiveIdx(0);
    } catch { setSuggestions([]); }
  }, []);

  const handleInput = (e) => {
    const newVal = e.target.value;
    onChange(newVal);

    const cursorPos = e.target.selectionStart;
    // Detect @ trigger
    const textBefore = newVal.slice(0, cursorPos);
    const lastAt = textBefore.lastIndexOf('@');

    if (lastAt >= 0) {
      const charBefore = lastAt > 0 ? textBefore[lastAt - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || lastAt === 0) {
        const q = textBefore.slice(lastAt + 1);
        if (q.length >= 0 && !q.includes(' ')) {
          setMentionStart(lastAt);
          setQuery(q);
          setShowSuggestions(true);
          clearTimeout(searchTimeout.current);
          searchTimeout.current = setTimeout(() => doSearch(q), 200);
          return;
        }
      }
    }
    setShowSuggestions(false);
  };

  const insertMention = (item) => {
    const before = value.slice(0, mentionStart);
    const after = value.slice(inputRef.current?.selectionStart || value.length);
    const token = `{{mention:${item.type}:${item.id}:${item.display}}}`;
    const newVal = before + token + ' ' + after;
    onChange(newVal);
    setShowSuggestions(false);
    setQuery('');
    inputRef.current?.focus();
  };

  const handlePaste = (e) => {
    if (!onPasteImage) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) onPasteImage(file);
        return;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit?.();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Group suggestions by type
  const grouped = suggestions.reduce((acc, s) => {
    acc[s.type] = acc[s.type] || [];
    acc[s.type].push(s);
    return acc;
  }, {});

  return (
    <div className="relative flex-1">
      <Input
        ref={inputRef}
        value={value.replace(/\{\{mention:\w+:[^:]+:([^}]*)\}\}/g, '@$1')}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder || 'Type a message... Use @ to mention'}
        disabled={disabled}
        className="flex-1"
        data-testid="channel-message-input"
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute bottom-full mb-1 left-0 right-0 bg-popover border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50"
          data-testid="mention-suggestions"
        >
          {Object.entries(grouped).map(([type, items]) => {
            const Icon = CATEGORY_ICONS[type] || User;
            return (
              <div key={type}>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" />
                  {CATEGORY_LABELS[type] || type}
                </div>
                {items.map((item) => {
                  const globalIdx = suggestions.indexOf(item);
                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        globalIdx === activeIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50'
                      }`}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(item); }}
                      onMouseEnter={() => setActiveIdx(globalIdx)}
                      data-testid={`mention-item-${item.type}-${item.id}`}
                    >
                      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate font-medium">{item.display}</span>
                      {item.secondary && item.secondary !== item.display && (
                        <span className="text-xs text-muted-foreground ml-auto shrink-0">{item.secondary}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
